import { CustomField, customFieldConstants, CustomFieldFieldLabels } from "../../api/CustomFields/CustomFields";
import { EntityDetailProps } from "../../common/entity/EntityInterfaces";
import Detail from "../../common/panels/Detail/Detail";
import {
  DetailsPageTopControlsView,
  TagProps,
} from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { CustomTypeTemplateTile } from "../../common/sidebar/tiles/CustomTypeTemplateTile";
import { EntitySidebarTileAutosorter } from "../../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { CustomFieldsDetailTable } from "../CustomFields/CustomFieldsDetailView";
import { useCustomTypeTemplateManagerRoutes } from "../CustomTypeTemplates/CustomTypeTemplateManager";
import { useContext } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { customFieldTemplateConstants } from "../../api/CustomFields/CustomFieldTemplates";
import { showtoast } from "../../common/overlays/Toasts/showtoast";
import { useQueryClient } from "@tanstack/react-query";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { Button } from "../../common/buttons/Button/Button";
import { usePermissions } from "../../common/permissions/useEntityPermissions";

export const CustomFieldTemplateRenderer = (props: EntityDetailProps<"customFieldTemplates">) => {
  return <CustomFieldTemplatesDetail {...props} />;
};

export const CustomFieldTemplatesDetail = ({
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
  entityTypeId,
}: EntityDetailProps<"customFieldTemplates">) => {
  const { getTemplateManagerRouteDetails } = useCustomTypeTemplateManagerRoutes();
  const { api } = useContext(SessionContext);
  const queryClient = useQueryClient();
  const { customFields } = usePermissions().permissions;

  const tags: TagProps[] = [];
  if (entity.customFieldsFromCurrentTemplate && entity.customFieldsFromCurrentTemplate.length > 0) {
    tags.push({
      label: "Already in use",
      className: "label label-soft-info",
      title: "This template is already in use.",
    });
  }

  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          tags={tags}
          entity={entity}
          entityConstants={entityConstants}
          hideHistoryModal
          controls={
            <>
              <Button
                className="btn btn-primary flex align-center gap-5"
                style={{ minWidth: "max-content" }}
                onClick={() => {
                  api
                    .post(`${customFieldTemplateConstants.resource}/${entity.id}/create`, {})
                    .then((response) => {
                      showtoast("success", "Custom field created successfully.");
                      queryClient.invalidateQueries({ queryKey: [customFieldTemplateConstants.resource] });
                    })
                    .catch((e) => {
                      showtoast("error", e.message ?? "Failed to create custom field.");
                      console.error(e);
                    });
                }}
                disabled={!customFields.canCreate}
                title={
                  !customFields.canCreate
                    ? "No permission to create custom fields"
                    : "Create a new custom field based on this template"
                }
              >
                <div>
                  <LucideIcon name="plus" />
                </div>
                Create
              </Button>
            </>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <>
          <CustomFieldsDetailTable
            entityTypeId="customFields"
            entityConstants={customFieldConstants}
            entity={entity as CustomField}
            fieldLabels={CustomFieldFieldLabels}
            permissions={permissions as any}
          />
        </>
      }
      sidebar={
        <EntitySidebarTileAutosorter>
          <CustomTypeTemplateTile
            defaultFilters={{ customFieldTemplateIds: [entity.id] }}
            linkTo={(e) => getTemplateManagerRouteDetails(e.entityType, e.id)}
            excludeFilters={{ customFieldTemplateIds: () => false }}
          />
        </EntitySidebarTileAutosorter>
      }
    />
  );
};
